import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import Navbar from './Navbar';

const DEFAULT_WEB_THEME = {
  primaryColor: '#780202',
  topbarColor: '#780202',
  topbarTextColor: '#FFCA08',
  sidebarBgColor: '#FFFFFF',
  sidebarTextColor: '#1F2937',
  sidebarActiveColor: '#FFF3BF',
  pageBgColor: '#F6F7FB',
  cardBgColor: '#FFFFFF',
  buttonColor: '#780202',
  buttonTextColor: '#FFFFFF',
  fontFamily: 'Roboto',
  borderRadius: 4,
};

const COLOR_FIELDS = [
  ['primaryColor', 'Primary Color'],
  ['topbarColor', 'Top Navbar Color'],
  ['topbarTextColor', 'Top Navbar Text'],
  ['sidebarBgColor', 'Sidebar Background'],
  ['sidebarTextColor', 'Sidebar Text'],
  ['sidebarActiveColor', 'Sidebar Active Item'],
  ['pageBgColor', 'Page Background'],
  ['cardBgColor', 'Card Background'],
  ['buttonColor', 'Button Color'],
  ['buttonTextColor', 'Button Text'],
];

const FONT_OPTIONS = ['Roboto', 'Arial', 'Inter', 'Poppins', 'Tahoma'];

export default function WebAppThemeSettings() {
  const [settings, setSettings] = useState(DEFAULT_WEB_THEME);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const cmpID = user?.cmpID || 1;

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${global.SET_API_URL}webTheme/public`, {
          params: { cmpID },
        });
        const data = response?.data?.data;
        if (data) {
          setSettings({
            primaryColor: data.primaryColor || DEFAULT_WEB_THEME.primaryColor,
            topbarColor: data.topbarColor || DEFAULT_WEB_THEME.topbarColor,
            topbarTextColor: data.topbarTextColor || DEFAULT_WEB_THEME.topbarTextColor,
            sidebarBgColor: data.sidebarBgColor || DEFAULT_WEB_THEME.sidebarBgColor,
            sidebarTextColor: data.sidebarTextColor || DEFAULT_WEB_THEME.sidebarTextColor,
            sidebarActiveColor: data.sidebarActiveColor || DEFAULT_WEB_THEME.sidebarActiveColor,
            pageBgColor: data.pageBgColor || DEFAULT_WEB_THEME.pageBgColor,
            cardBgColor: data.cardBgColor || DEFAULT_WEB_THEME.cardBgColor,
            buttonColor: data.buttonColor || DEFAULT_WEB_THEME.buttonColor,
            buttonTextColor: data.buttonTextColor || DEFAULT_WEB_THEME.buttonTextColor,
            fontFamily: data.fontFamily || DEFAULT_WEB_THEME.fontFamily,
            borderRadius: Number(data.borderRadius) || DEFAULT_WEB_THEME.borderRadius,
          });
        }
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to load web app theme settings.' });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [cmpID]);

  const previewStyle = useMemo(() => ({
    fontFamily: settings.fontFamily,
    borderRadius: Number(settings.borderRadius) || 0,
  }), [settings.borderRadius, settings.fontFamily]);

  const handleChange = (key) => (event) => {
    setSettings((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleRadiusChange = (_, value) => {
    setSettings((prev) => ({ ...prev, borderRadius: value }));
  };

  const handleReset = () => {
    setSettings(DEFAULT_WEB_THEME);
    setMessage({ type: '', text: '' });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await axios.post(`${global.SET_API_URL}webTheme/upsert`, {
        cmpID,
        ...settings,
        updatedBy: user?.userName || user?.dispName || 'web-admin',
      });

      if (response?.data?.data) {
        setSettings((prev) => ({ ...prev, ...response.data.data }));
      }

      window.dispatchEvent(new CustomEvent('webThemeSettingsUpdated', { detail: { cmpID } }));
      setMessage({ type: 'success', text: 'Web app theme saved successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box sx={{ minHeight: '100vh', p: 2, bgcolor: 'var(--web-theme-page-bg, #F6F7FB)' }}>
        <Card sx={{ maxWidth: 1180, mx: 'auto', borderRadius: 'var(--web-theme-radius, 4px)' }}>
          <CardContent>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Web App Theme Settings
            </Typography>

            {message.text ? (
              <Alert severity={message.type === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>
                {message.text}
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1.5,
                    }}
                  >
                    {COLOR_FIELDS.map(([key, label]) => (
                      <TextField
                        key={key}
                        label={label}
                        type="color"
                        value={settings[key]}
                        onChange={handleChange(key)}
                        InputLabelProps={{ shrink: true }}
                        disabled={loading}
                        size="small"
                      />
                    ))}
                  </Box>

                  <FormControl fullWidth size="small">
                    <InputLabel id="web-font-family-label">Font Family</InputLabel>
                    <Select
                      labelId="web-font-family-label"
                      value={settings.fontFamily}
                      label="Font Family"
                      onChange={handleChange('fontFamily')}
                      disabled={loading}
                    >
                      {FONT_OPTIONS.map((font) => (
                        <MenuItem key={font} value={font}>{font}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      Border Radius: {settings.borderRadius}px
                    </Typography>
                    <Slider
                      value={Number(settings.borderRadius) || 0}
                      min={0}
                      max={16}
                      step={1}
                      onChange={handleRadiusChange}
                      disabled={loading}
                    />
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
                      {saving ? 'Saving...' : 'Save Theme'}
                    </Button>
                    <Button variant="outlined" onClick={handleReset} disabled={loading || saving}>
                      Reset
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Live Web Preview
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #CBD5E1',
                    overflow: 'hidden',
                    bgcolor: settings.pageBgColor,
                    boxShadow: 2,
                    ...previewStyle,
                  }}
                >
                  <Box sx={{ bgcolor: settings.topbarColor, color: settings.topbarTextColor, px: 2, py: 1.4 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={800}>Lucky POS</Typography>
                      <Typography fontWeight={700}>Welcome Admin</Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" sx={{ minHeight: 300 }}>
                    <Box sx={{ width: 170, bgcolor: settings.sidebarBgColor, color: settings.sidebarTextColor, p: 1.2 }}>
                      {['Dashboard', 'POS', 'Items', 'Reports'].map((item, index) => (
                        <Box
                          key={item}
                          sx={{
                            px: 1.2,
                            py: 0.8,
                            mb: 0.6,
                            bgcolor: index === 1 ? settings.sidebarActiveColor : 'transparent',
                            color: settings.sidebarTextColor,
                            borderRadius: `${settings.borderRadius}px`,
                            fontWeight: index === 1 ? 800 : 600,
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ flex: 1, p: 2 }}>
                      <Box sx={{ bgcolor: settings.cardBgColor, p: 2, mb: 1.5, border: '1px solid #E2E8F0', ...previewStyle }}>
                        <Typography variant="h6" fontWeight={800} color={settings.primaryColor}>
                          Sales Overview
                        </Typography>
                        <Typography variant="body2" color="#64748B">
                          Theme colors apply to navbar, sidebar, buttons, cards, and page background.
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          sx={{
                            bgcolor: settings.buttonColor,
                            color: settings.buttonTextColor,
                            borderRadius: `${settings.borderRadius}px`,
                            '&:hover': { bgcolor: settings.buttonColor },
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          sx={{
                            borderColor: settings.primaryColor,
                            color: settings.primaryColor,
                            borderRadius: `${settings.borderRadius}px`,
                          }}
                        >
                          Preview
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
